import { useEffect, useId, useRef, useState } from 'react';
import { Icon } from '../../components/Icon';
import { usePresence } from '../../hooks/usePresence';
import { useI18n } from '../../i18n/i18n-context';
import { cx } from '../../lib/cx';

export interface WritingAid {
  readonly label: string;
  /** What it does, under its name. */
  readonly hint: string;
  readonly on: boolean;
  readonly onChange: (on: boolean) => void;
}

/**
 * One aid's row: its name and what it does, then its switch, which alone turns it on or off. The
 * draft's saving has one of these too.
 */
export function Aid({ label, hint, on, onChange }: WritingAid) {
  const id = useId();
  return (
    <div className="writing-aid">
      <span className="writing-aid-text">
        <span className="writing-aid-label" id={`${id}-label`}>
          {label}
        </span>
        <span className="writing-aid-hint" id={`${id}-hint`}>
          {hint}
        </span>
      </span>
      <input
        type="checkbox"
        role="switch"
        className="switch"
        checked={on}
        aria-labelledby={`${id}-label`}
        aria-describedby={`${id}-hint`}
        onChange={(event) => {
          onChange(event.target.checked);
        }}
      />
    </div>
  );
}

/**
 * The editor's help with English as it's written, behind one button in its toolbar: a panel of
 * switches, each saying what it does.
 */
export function WritingAids({ aids }: { readonly aids: readonly WritingAid[] }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const { present, leaving } = usePresence(open);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Captured first, so the editor's own Escape (which leaves full screen) never sees it.
      event.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  return (
    <div
      className="writing-aids"
      ref={menuRef}
      onBlur={(event) => {
        // Tabbing out of the panel closes it. Focus going nowhere, as on a click on an aid's own
        // text or the window losing focus, doesn't: a click elsewhere closes it already.
        const next = event.relatedTarget;
        if (next instanceof Node && !event.currentTarget.contains(next)) setOpen(false);
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        className="toolbar-button"
        aria-label={t.editor.writingAids}
        title={t.editor.writingAids}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => {
          setOpen((current) => !current);
        }}
      >
        <Icon name="letters" />
        <span className="toolbar-button-text">{t.editor.writingAids}</span>
        <span className="writing-aids-chevron" aria-hidden="true">
          <Icon name="chevron" />
        </span>
      </button>
      {present && (
        <div
          className={cx('writing-aids-panel', leaving && 'is-leaving')}
          id={panelId}
          inert={leaving}
          role="dialog"
          aria-label={t.editor.writingAids}
        >
          <p className="writing-aids-heading" aria-hidden="true">
            {t.editor.writingAids}
          </p>
          {aids.map((aid) => (
            <Aid key={aid.label} {...aid} />
          ))}
          <p className="writing-aids-note">{t.editor.writingAidsNote}</p>
        </div>
      )}
    </div>
  );
}
